import { request, setCsrfToken, type ApiResponse } from "./client";
import { rememberEtag } from "./etag";
import type { CursorPage } from "../query/cursor.svelte";
import type {
  ActivationDelivery,
  AdminAccessCode,
  AdminAccessSession,
  AdminAccessSessionPageMeta,
  AdminAccessSessionStatus,
  AdminAccessSessionSummary,
  AdminUpgradeFunnelCount,
  AdminUpgradeSession,
  AdminUpgradeSessionPageMeta,
  AdminUpgradeSessionStatus,
  AdminAuditEvent,
  AdminAuditPageMeta,
  AdminCustomerSubscription,
  AdminInvoice,
  AdminInvoiceCreateRequest,
  AdminInvoiceCreation,
  AuditEvent,
  AuthData,
  AuthResult,
  BillingPortalSession,
  BillingPlansData,
  BillingSummary,
  BillingSummaryWithSeats,
  BuildersAddon,
  BuildersAddonSession,
  BuildersAddonSessionRequest,
  ClaimResult,
  DashboardData,
  EntitlementAccessLevel,
  EntitlementCustomer,
  EntitlementCustomerStatus,
  RevenueReport,
  EntitlementSync,
  ExtensionAccess,
  ExtensionActivation,
  ExtensionPolicy,
  ExtensionPolicyCatalog,
  ExtensionPolicyMeta,
  ExtensionPolicyUpdateRequest,
  Domain,
  DomainChallenge,
  DiscordInvitation,
  DiscordInvitationStatus,
  DiscordLink,
  DiscordLinkStart,
  DiscordReconcilePlan,
  EntitlementFreshness,
  Invite,
  InviteListItem,
  IpPolicy,
  IpPolicyUpdateRequest,
  Member,
  ManagedEntitlement,
  PageMeta,
  Role,
  SamlConnection,
  SeatAssignment,
  SeatBatch,
  SeatBatchRequest,
  PolicyMeta,
  ProtectedDomain,
  PublicUpgradeIntent,
  SessionData,
  Settings,
  SsoDiscovery,
  SubscriptionChange,
  SupportOrganization,
  SupportOrganizationCreationRequest,
  SupportOrganizationCustomerRequest,
  PortalEmailLink,
  UpgradeFulfillment,
  UpgradeFulfillmentRequest,
  UpgradeSession,
  UpgradeRequest,
  SeatInsights,
  SeatSession,
  SeatSessionRequest
} from "./contract";
import { normalizeBillingSummary } from "../billing/summary";

// ETag registry keys for resources whose write path needs the last GET's ETag.
export const etagKey = {
  saml: (orgId: string) => `saml:${orgId}`,
  settings: (orgId: string) => `settings:${orgId}`,
  dashboard: (orgId: string) => `dashboard:${orgId}`,
  ipPolicy: (orgId: string) => `access-policy:ip:${orgId}`,
  protectedDomain: (orgId: string) => `access-policy:protected-domain:${orgId}`,
  extensionPolicy: (orgId: string) => `extension-policy:${orgId}`
};

// ---------------------------------------------------------------- auth / session

export async function getAuthMethods(): Promise<{ devPasswordLogin: boolean }> {
  return (await request<{ devPasswordLogin: boolean }>("/v1/auth/methods")).data;
}

export async function loginWithDevPassword(email: string, password: string): Promise<AuthData> {
  const res = await request<AuthData>("/v1/auth/dev-password", {
    method: "POST", body: { email, password }, csrf: false
  });
  setCsrfToken(res.data.csrfToken);
  return res.data;
}

/**
 * Request a passwordless email verification link (Resend-backed). Public and
 * enumeration-safe: the server always answers 202. New accounts are created
 * automatically once the recipient verifies — there are no passwords.
 */
export async function startEmailVerification(body: {
  email: string;
  returnTo?: string;
  inviteToken?: string;
}): Promise<{ accepted: true; requestId: string }> {
  const res = await request<{ accepted: true; requestId: string }>("/v1/auth/email/start", {
    method: "POST",
    body,
    csrf: false
  });
  return res.data;
}

/** Consume a verification token and establish the cookie session. */
export async function verifyEmail(token: string): Promise<AuthData & { returnTo?: string }> {
  const res = await request<AuthData & { returnTo?: string }>("/v1/auth/email/verify", {
    method: "POST",
    body: { token },
    csrf: false
  });
  setCsrfToken(res.data.csrfToken);
  return res.data;
}

/** Consume the copy/paste alternative for a specific email verification request. */
export async function verifyEmailCode(code: string, requestId: string): Promise<AuthData & { returnTo?: string }> {
  const res = await request<AuthData & { returnTo?: string }>("/v1/auth/email/verify", {
    method: "POST",
    body: { code, requestId },
    csrf: false
  });
  setCsrfToken(res.data.csrfToken);
  return res.data;
}

export async function discoverSso(email: string): Promise<SsoDiscovery> {
  const res = await request<SsoDiscovery>("/v1/auth/sso/discover", {
    method: "POST",
    body: { email },
    csrf: false
  });
  return res.data;
}

export async function getSession(signal?: AbortSignal): Promise<SessionData> {
  const res = await request<SessionData>("/v1/session", signal ? { signal } : {});
  setCsrfToken(res.data.csrfToken);
  return res.data;
}

export async function logout(): Promise<void> {
  await request("/v1/auth/logout", { method: "POST", expect: "none" });
  setCsrfToken(null);
}

export async function consumeAuthResult(handle: string): Promise<AuthResult> {
  const res = await request<AuthResult>(`/v1/auth/results/${encodeURIComponent(handle)}`, { csrf: false });
  return res.data;
}

/** Re-discover purchases for the verified session email (Entitlements-backed). */
export async function syncEntitlements(): Promise<EntitlementSync> {
  const res = await request<EntitlementSync>("/v1/entitlements/sync", { method: "POST" });
  return res.data;
}

/** Load current-user activation options for an extension-directed portal sign-in. */
export async function getExtensionAccess(signal?: AbortSignal): Promise<ExtensionAccess> {
  const res = await request<ExtensionAccess>("/v1/extension/access", signal ? { signal } : {});
  return res.data;
}

/** Request delivery without ever returning the one-time activation key to the browser. */
export async function requestExtensionActivation(
  organizationId: string,
  idempotencyKey: string
): Promise<ExtensionActivation> {
  const res = await request<ExtensionActivation>("/v1/extension/access/activation", {
    method: "POST",
    body: { organizationId },
    idempotencyKey
  });
  return res.data;
}

export async function redeemClaim(
  body: { claimToken: string; organizationName: string },
  idempotencyKey: string
): Promise<ClaimResult> {
  const res = await request<ClaimResult>("/v1/enterprise/claims/redeem", {
    method: "POST",
    body,
    idempotencyKey
  });
  return res.data;
}

// ----------------------------------------------------- support administration

export interface EntitlementCustomerQuery {
  cursor?: string;
  /** 1–100; the server defaults to 50 when omitted. */
  limit?: number;
  /** Server-side match over customer name and external key (max 200 chars). */
  query?: string;
  status?: EntitlementCustomerStatus | "trial";
  plan?: "community" | "pro" | "team" | "builders" | "enterprise";
  /** Stable server-side ordering, applied before cursor paging. */
  sort?: EntitlementCustomerSort;
  direction?: SortDirection;
}

export type EntitlementCustomerSort = "name" | "externalKey" | "status" | "updatedAt";

export async function getAdminRevenueReport(signal?: AbortSignal): Promise<RevenueReport> {
  const res = await request<RevenueReport>("/v1/admin/revenue", { ...(signal ? { signal } : {}) });
  return res.data;
}

export interface AdminUpgradeSessionPage {
  items: AdminUpgradeSession[];
  available: boolean;
  counts: AdminUpgradeFunnelCount[];
  nextCursor: string | null;
  totalCount: number;
}

export async function listAdminUpgradeSessions(
  query: {
    cursor?: string;
    limit?: number;
    since?: string;
    status?: AdminUpgradeSessionStatus;
  } = {},
  signal?: AbortSignal
): Promise<AdminUpgradeSessionPage> {
  const res = await request<AdminUpgradeSession[], AdminUpgradeSessionPageMeta>(
    "/v1/admin/upgrade-sessions",
    { query, ...(signal ? { signal } : {}) }
  );
  return {
    items: res.data,
    available: res.meta.available,
    counts: res.meta.counts,
    nextCursor: res.meta.nextCursor,
    totalCount: res.meta.totalCount
  };
}

export interface AdminAuditQuery {
  cursor?: string;
  limit?: number;
  actorKind?: string;
  action?: string;
  customerId?: string;
  organizationId?: string;
  from?: string;
  to?: string;
}

export async function listAdminAuditEvents(
  query: AdminAuditQuery = {},
  signal?: AbortSignal
): Promise<{ items: AdminAuditEvent[]; nextCursor: string | null }> {
  const res = await request<AdminAuditEvent[], AdminAuditPageMeta>("/v1/admin/audit", {
    query: { ...query },
    ...(signal ? { signal } : {})
  });
  return { items: res.data, nextCursor: res.meta.nextCursor };
}

export async function listAdminAccessCodes(customerId: string, signal?: AbortSignal): Promise<AdminAccessCode[]> {
  const res = await request<AdminAccessCode[]>(`/v1/admin/entitlement-customers/${encodeURIComponent(customerId)}/access-codes`, {
    ...(signal ? { signal } : {})
  });
  return res.data;
}

export interface AdminAccessSessionPage {
  items: AdminAccessSession[];
  summary: AdminAccessSessionSummary;
  nextCursor: string | null;
  totalCount: number;
}

export async function listAdminAccessSessions(
  customerId: string,
  query: { cursor?: string; limit?: number; status?: AdminAccessSessionStatus } = {},
  signal?: AbortSignal
): Promise<AdminAccessSessionPage> {
  const res = await request<AdminAccessSession[], AdminAccessSessionPageMeta>(
    `/v1/admin/entitlement-customers/${encodeURIComponent(customerId)}/access-sessions`,
    { query, ...(signal ? { signal } : {}) }
  );
  return {
    items: res.data,
    summary: res.meta.summary,
    nextCursor: res.meta.nextCursor,
    totalCount: res.meta.totalCount
  };
}
export type SortDirection = "asc" | "desc";

/**
 * Cursor-paginated. Search, status filtering, and sorting happen in the Entitlements service,
 * so the browser never holds the whole customer set: `totalCount` from the page meta
 * — not `items.length` — is the authority for how many customers match.
 */
export async function listEntitlementCustomers(
  query: EntitlementCustomerQuery = {},
  signal?: AbortSignal
): Promise<CursorPage<EntitlementCustomer>> {
  const res = await request<EntitlementCustomer[], PageMeta>("/v1/admin/entitlement-customers", {
    query: { ...query },
    ...(signal ? { signal } : {})
  });
  return { items: res.data, nextCursor: res.meta.nextCursor, totalCount: res.meta.totalCount };
}

/**
 * Reads one customer directly so the detail page works on a cold deep link,
 * reload, or back/forward step without the list ever having been fetched.
 */
export async function getEntitlementCustomer(
  customerId: string,
  signal?: AbortSignal
): Promise<EntitlementCustomer> {
  const res = await request<EntitlementCustomer>(
    `/v1/admin/entitlement-customers/${encodeURIComponent(customerId)}`,
    signal ? { signal } : {}
  );
  return res.data;
}

export async function createEntitlementCustomer(
  body: { externalKey: string; name: string; status: EntitlementCustomerStatus },
  idempotencyKey: string
): Promise<EntitlementCustomer> {
  const res = await request<EntitlementCustomer>("/v1/admin/entitlement-customers", {
    method: "POST",
    body,
    idempotencyKey
  });
  return res.data;
}

export async function updateEntitlementCustomer(
  customerId: string,
  status: EntitlementCustomerStatus,
  idempotencyKey: string
): Promise<EntitlementCustomer> {
  const res = await request<EntitlementCustomer>(
    `/v1/admin/entitlement-customers/${encodeURIComponent(customerId)}`,
    { method: "PATCH", body: { status }, idempotencyKey }
  );
  return res.data;
}

export async function listManagedEntitlements(
  customerId: string,
  signal?: AbortSignal
): Promise<ManagedEntitlement[]> {
  const res = await request<ManagedEntitlement[]>(
    `/v1/admin/entitlement-customers/${encodeURIComponent(customerId)}/entitlements`,
    signal ? { signal } : {}
  );
  return res.data;
}

export async function listSubscriptionChanges(
  customerId: string,
  signal?: AbortSignal
): Promise<SubscriptionChange[]> {
  const res = await request<SubscriptionChange[]>(
    `/v1/admin/entitlement-customers/${encodeURIComponent(customerId)}/subscription-history`,
    signal ? { signal } : {}
  );
  return res.data;
}

/**
 * The customer's current extension plan subscription, or null when there is none.
 * `modifiable` / `notModifiableReason` decide whether a modify quote can be composed.
 */
export async function getAdminCustomerSubscription(
  customerId: string,
  signal?: AbortSignal
): Promise<AdminCustomerSubscription | null> {
  const res = await request<{ subscription: AdminCustomerSubscription | null }>(
    `/v1/admin/entitlement-customers/${encodeURIComponent(customerId)}/subscription`,
    signal ? { signal } : {}
  );
  return res.data.subscription;
}

export async function listAdminInvoices(customerId: string, signal?: AbortSignal): Promise<AdminInvoice[]> {
  const res = await request<AdminInvoice[]>(
    `/v1/admin/entitlement-customers/${encodeURIComponent(customerId)}/invoices`,
    signal ? { signal } : {}
  );
  return res.data;
}

/**
 * Support invoicing creates a recurring Stripe subscription. Access level and
 * entitlement expiry are derived server-side from the plan and the subscription
 * period, so this shape deliberately has no `accessLevel`/`expiresAt` — the exact
 * generated request type keeps them unsendable.
 */
export type AdminInvoiceRequest = AdminInvoiceCreateRequest & { notes: string | null };

export async function createAdminInvoice(
  customerId: string,
  body: AdminInvoiceRequest,
  idempotencyKey: string
): Promise<AdminInvoiceCreation> {
  const res = await request<AdminInvoiceCreation>(
    `/v1/admin/entitlement-customers/${encodeURIComponent(customerId)}/invoices`,
    { method: "POST", body, idempotencyKey }
  );
  return res.data;
}

export async function createManagedEntitlement(
  customerId: string,
  body: {
    accessLevel: EntitlementAccessLevel;
    expiresAt: string | null;
    maxDevices: number;
    notes: string | null;
  },
  idempotencyKey: string
): Promise<ManagedEntitlement> {
  const res = await request<ManagedEntitlement>(
    `/v1/admin/entitlement-customers/${encodeURIComponent(customerId)}/entitlements`,
    { method: "POST", body, idempotencyKey }
  );
  return res.data;
}

export async function linkEntitlementCustomerEmail(
  customerId: string,
  email: string,
  idempotencyKey: string
): Promise<PortalEmailLink> {
  const res = await request<PortalEmailLink>(
    `/v1/admin/entitlement-customers/${encodeURIComponent(customerId)}/portal-email-link`,
    { method: "PUT", body: { email }, idempotencyKey }
  );
  return res.data;
}

// -------------------------------------------------------- Discord invitations

export async function getDiscordInvitation(
  orgId: string,
  signal?: AbortSignal
): Promise<DiscordInvitation | null> {
  const res = await request<DiscordInvitation | null>(
    `/v1/orgs/${encodeURIComponent(orgId)}/discord-invitation`,
    signal ? { signal } : {}
  );
  return res.data;
}

export async function resendDiscordInvitation(
  orgId: string,
  idempotencyKey: string
): Promise<DiscordInvitation> {
  const res = await request<DiscordInvitation>(
    `/v1/orgs/${encodeURIComponent(orgId)}/discord-invitation/resend`,
    { method: "POST", idempotencyKey }
  );
  return res.data;
}

/**
 * The caller's own Builders Discord account link (OAuth2). Any member the Builders plan
 * or add-on covers may link; a member it does not cover is refused server-side. A
 * `404 discord_oauth_disabled` means linking is switched off.
 */
export async function getDiscordLink(orgId: string, signal?: AbortSignal): Promise<DiscordLink> {
  const res = await request<DiscordLink>(
    `/v1/orgs/${encodeURIComponent(orgId)}/discord-link`,
    signal ? { signal } : {}
  );
  return res.data;
}

/** One-time Discord authorization URL; the callback returns to Overview with `?discord=`. */
export async function startDiscordLink(orgId: string): Promise<DiscordLinkStart> {
  const res = await request<DiscordLinkStart>(
    `/v1/orgs/${encodeURIComponent(orgId)}/discord-link/start`,
    { method: "POST" }
  );
  return res.data;
}

export async function deleteDiscordLink(orgId: string): Promise<DiscordLink> {
  const res = await request<DiscordLink>(`/v1/orgs/${encodeURIComponent(orgId)}/discord-link`, { method: "DELETE" });
  return res.data;
}

export async function listDiscordInvitations(
  query: { status?: DiscordInvitationStatus; cursor?: string; limit?: number } = {},
  signal?: AbortSignal
): Promise<CursorPage<DiscordInvitation>> {
  const res = await request<DiscordInvitation[], PageMeta>("/v1/admin/discord/invitations", {
    query,
    ...(signal ? { signal } : {})
  });
  return { items: res.data, nextCursor: res.meta.nextCursor, totalCount: res.meta.totalCount };
}

export interface AdminDiscordOpsSummary {
  pending: number;
  sent: number;
  accepted: number;
  failed: number;
}

export async function getAdminDiscordOpsSummary(signal?: AbortSignal): Promise<AdminDiscordOpsSummary> {
  const [pending, sent, accepted, failed] = await Promise.all([
    listDiscordInvitations({ status: "pending", limit: 1 }, signal),
    listDiscordInvitations({ status: "sent", limit: 1 }, signal),
    listDiscordInvitations({ status: "accepted", limit: 1 }, signal),
    listDiscordInvitations({ status: "failed", limit: 1 }, signal)
  ]);
  return {
    pending: pending.totalCount,
    sent: sent.totalCount,
    accepted: accepted.totalCount,
    failed: failed.totalCount
  };
}

export async function adminResendDiscordInvitation(
  invitationId: string,
  idempotencyKey: string
): Promise<DiscordInvitation> {
  const res = await request<DiscordInvitation>(
    `/v1/admin/discord/invitations/${encodeURIComponent(invitationId)}/resend`,
    { method: "POST", idempotencyKey }
  );
  return res.data;
}

export async function reconcileDiscordInvitations(
  input: { dryRun: boolean; confirm?: string },
  idempotencyKey: string
): Promise<DiscordReconcilePlan> {
  const res = await request<DiscordReconcilePlan>("/v1/admin/discord/invitations/reconcile", {
    method: "POST",
    body: input,
    idempotencyKey
  });
  return res.data;
}

// ----------------------------------------------------------------------- billing

export async function getBillingSummary(orgId: string, signal?: AbortSignal): Promise<BillingSummaryWithSeats> {
  // The seat fields are optional in the shared contract during rolling deploys; the
  // normalizer fills them in deterministically so the UI never renders undefined or NaN.
  const res = await request<BillingSummary>(`/v1/orgs/${orgId}/billing`, signal ? { signal } : {});
  return normalizeBillingSummary(res.data);
}

export async function createBillingPortalSession(
  orgId: string,
  idempotencyKey: string
): Promise<BillingPortalSession> {
  const res = await request<BillingPortalSession>(`/v1/orgs/${orgId}/billing/portal-session`, {
    method: "POST",
    idempotencyKey
  });
  return res.data;
}

export async function getBillingPlans(orgId: string, signal?: AbortSignal): Promise<BillingPlansData> {
  const res = await request<BillingPlansData>(
    `/v1/orgs/${encodeURIComponent(orgId)}/billing/plans`,
    signal ? { signal } : {}
  );
  return res.data;
}

export async function createUpgradeSession(
  orgId: string,
  input: UpgradeRequest,
  idempotencyKey: string
): Promise<UpgradeSession> {
  const res = await request<UpgradeSession>(
    `/v1/orgs/${encodeURIComponent(orgId)}/billing/upgrade-session`,
    { method: "POST", body: input, idempotencyKey }
  );
  return res.data;
}

/**
 * Owner-only Team seat-quantity change. The response is a Stripe-hosted confirmation
 * where the owner reviews the prorated change; nothing is billed and no seat count
 * moves until Stripe confirms and the webhook lands, so callers must re-read billing
 * and licence counts on return rather than assuming success from the navigation.
 */
export async function createSeatSession(
  orgId: string,
  input: SeatSessionRequest,
  idempotencyKey: string
): Promise<SeatSession> {
  const res = await request<SeatSession>(
    `/v1/orgs/${encodeURIComponent(orgId)}/billing/seat-session`,
    { method: "POST", body: input, idempotencyKey }
  );
  return res.data;
}

// ------------------------------------------------------------- Builders add-on

/**
 * The Builders add-on offer, purchase, and seat-assignment state for an organization.
 * Any active member may read it; members without `billing.manage` / `seats.read` get the
 * offer basics and their own `viewer` only. A `404 feature_disabled` means the add-on is
 * switched off server-side and nothing add-on related should render.
 */
export async function getBuildersAddon(orgId: string, signal?: AbortSignal): Promise<BuildersAddon> {
  const res = await request<BuildersAddon>(
    `/v1/orgs/${encodeURIComponent(orgId)}/builders-addon`,
    signal ? { signal } : {}
  );
  return res.data;
}

/**
 * Owner-only: a Stripe Checkout (first purchase) or hosted subscription-update
 * confirmation (seat quantity change). The body is exactly `{ quantity }`; nothing is
 * billed and no state moves until Stripe confirms and the webhook lands, so callers
 * re-read the add-on on return rather than assuming success from the navigation.
 */
export async function createBuildersAddonSession(
  orgId: string,
  input: BuildersAddonSessionRequest,
  idempotencyKey: string
): Promise<BuildersAddonSession> {
  const res = await request<BuildersAddonSession>(
    `/v1/orgs/${encodeURIComponent(orgId)}/builders-addon/session`,
    { method: "POST", body: { quantity: input.quantity }, idempotencyKey }
  );
  return res.data;
}

/**
 * Assign one purchased add-on seat to a member (`seats.manage`). `ifMatch` is the add-on
 * state's `etag`; a stale one answers `412 version_conflict` and the caller re-reads.
 * Returns the fresh add-on state, whose `etag` guards the next change.
 */
export async function assignBuildersAddonSeat(
  orgId: string,
  userId: string,
  ifMatch: string,
  idempotencyKey: string
): Promise<BuildersAddon> {
  const res = await request<BuildersAddon>(
    `/v1/orgs/${encodeURIComponent(orgId)}/builders-addon/assignments/${encodeURIComponent(userId)}`,
    { method: "PUT", ifMatch, idempotencyKey }
  );
  return res.data;
}

/** Release a member's add-on seat back to the pool. Same guards as assignment. */
export async function revokeBuildersAddonSeat(
  orgId: string,
  userId: string,
  ifMatch: string,
  idempotencyKey: string
): Promise<BuildersAddon> {
  const res = await request<BuildersAddon>(
    `/v1/orgs/${encodeURIComponent(orgId)}/builders-addon/assignments/${encodeURIComponent(userId)}`,
    { method: "DELETE", ifMatch, idempotencyKey }
  );
  return res.data;
}

/**
 * Current seat-assignment, activation, and valid-receipt counts for owners and admins
 * of Team and Enterprise organizations (`insights.read`). Device counts are valid
 * extension activations, not online presence.
 */
export async function getSeatInsights(orgId: string, signal?: AbortSignal): Promise<SeatInsights> {
  const res = await request<SeatInsights>(
    `/v1/orgs/${encodeURIComponent(orgId)}/insights`,
    signal ? { signal } : {}
  );
  return res.data;
}

/**
 * Session-free upgrade status for the public `/upgrade/complete` landing page. The
 * opaque `claim` is the only authorization, so this deliberately carries no session,
 * no CSRF, and no org id. An intent that has aged out answers 404 `intent_expired`
 * rather than a status value — callers render that rejection as the expired state.
 */
export async function getPublicUpgradeIntent(
  intentId: string,
  claim: string,
  signal?: AbortSignal
): Promise<PublicUpgradeIntent> {
  const res = await request<PublicUpgradeIntent>(
    `/v1/public/upgrade-intents/${encodeURIComponent(intentId)}`,
    { query: { claim }, ...(signal ? { signal } : {}) }
  );
  return res.data;
}

export async function confirmUpgradeFulfillment(
  orgId: string,
  input: UpgradeFulfillmentRequest,
  signal?: AbortSignal
): Promise<UpgradeFulfillment> {
  const res = await request<UpgradeFulfillment>(
    `/v1/orgs/${encodeURIComponent(orgId)}/billing/upgrade-fulfillment`,
    { method: "POST", body: input, ...(signal ? { signal } : {}) }
  );
  return res.data;
}

// ------------------------------------------------------------- dashboard / people

export type DashboardMeta = { entitlementFreshness: EntitlementFreshness };

export async function getDashboard(
  orgId: string,
  signal?: AbortSignal
): Promise<ApiResponse<DashboardData, DashboardMeta>> {
  const res = await request<DashboardData, DashboardMeta>(
    `/v1/orgs/${orgId}/dashboard`,
    signal ? { signal } : {}
  );
  rememberEtag(etagKey.dashboard(orgId), res.etag);
  return res;
}

export interface MemberQuery {
  cursor?: string;
  limit?: number;
  query?: string;
  role?: Role;
  status?: "active" | "suspended";
}

export async function listMembers(
  orgId: string,
  query: MemberQuery,
  signal?: AbortSignal
): Promise<ApiResponse<Member[], PageMeta>> {
  return request<Member[], PageMeta>(`/v1/orgs/${orgId}/members`, {
    query: { ...query },
    ...(signal ? { signal } : {})
  });
}

export async function createInvite(
  orgId: string,
  body: { email: string; role: "admin" | "member" },
  idempotencyKey: string
): Promise<Invite> {
  const res = await request<Invite>(`/v1/orgs/${orgId}/invites`, { method: "POST", body, idempotencyKey });
  return res.data;
}

/**
 * Pending + expired invitations for the org. The server masks the recipient and never
 * returns the invitation token, so nothing here can be replayed into an acceptance.
 */
export async function listInvites(orgId: string, signal?: AbortSignal): Promise<InviteListItem[]> {
  const res = await request<InviteListItem[]>(`/v1/orgs/${orgId}/invites`, signal ? { signal } : {});
  return res.data;
}

/**
 * Rotates the invitation token and re-queues delivery. There is no If-Match backstop,
 * so callers reuse ONE idempotency key per logical resend across manual retries.
 */
export async function resendInvite(
  orgId: string,
  inviteId: string,
  idempotencyKey: string
): Promise<Invite> {
  const res = await request<Invite>(`/v1/orgs/${orgId}/invites/${encodeURIComponent(inviteId)}/resend`, {
    method: "POST",
    idempotencyKey
  });
  return res.data;
}

export async function revokeInvite(
  orgId: string,
  inviteId: string,
  idempotencyKey: string
): Promise<{ revoked: boolean }> {
  const res = await request<{ revoked: boolean }>(`/v1/orgs/${orgId}/invites/${inviteId}`, {
    method: "DELETE",
    idempotencyKey
  });
  return res.data;
}

export type UpdatedMember = { version: number; role: Role; status: "active" | "suspended" } & Record<
  string,
  unknown
>;

export async function updateMember(
  orgId: string,
  userId: string,
  body: { role?: Role; status?: "active" | "suspended" },
  ifMatch: string,
  idempotencyKey: string
): Promise<UpdatedMember> {
  const res = await request<UpdatedMember>(`/v1/orgs/${orgId}/members/${userId}`, {
    method: "PATCH",
    body,
    ifMatch,
    idempotencyKey
  });
  return res.data;
}

export async function removeMember(
  orgId: string,
  userId: string,
  body: { seatEffect: "revoke" | "none" },
  ifMatch: string,
  idempotencyKey: string
): Promise<{ removed: boolean; seatRevoked: boolean }> {
  const res = await request<{ removed: boolean; seatRevoked: boolean }>(
    `/v1/orgs/${orgId}/members/${userId}`,
    { method: "DELETE", body, ifMatch, idempotencyKey }
  );
  return res.data;
}

export async function assignSeat(
  orgId: string,
  userId: string,
  ifMatch: string,
  idempotencyKey: string
): Promise<SeatAssignment> {
  const res = await request<SeatAssignment>(`/v1/orgs/${orgId}/seat-assignments/${userId}`, {
    method: "PUT",
    ifMatch,
    idempotencyKey
  });
  return res.data;
}

export async function revokeSeat(
  orgId: string,
  userId: string,
  ifMatch: string,
  idempotencyKey: string
): Promise<SeatAssignment> {
  const res = await request<SeatAssignment>(`/v1/orgs/${orgId}/seat-assignments/${userId}`, {
    method: "DELETE",
    ifMatch,
    idempotencyKey
  });
  return res.data;
}

export async function resendActivation(
  orgId: string,
  userId: string,
  idempotencyKey: string
): Promise<SeatAssignment> {
  const res = await request<SeatAssignment>(
    `/v1/orgs/${orgId}/seat-assignments/${userId}/resend-activation`,
    { method: "POST", idempotencyKey }
  );
  return res.data;
}

export async function batchSeats(
  orgId: string,
  body: SeatBatchRequest,
  idempotencyKey: string,
  ifMatch?: string
): Promise<SeatBatch> {
  const res = await request<SeatBatch>(`/v1/orgs/${orgId}/seat-assignments/batch`, {
    method: "POST",
    body,
    idempotencyKey,
    ...(ifMatch ? { ifMatch } : {})
  });
  return res.data;
}

// -------------------------------------------------------------- access policies

/**
 * IP allowlist + its propagation state. The strong ETag comes back on the response
 * header, so the write path reuses the captured value rather than reconstructing it.
 */
export async function getIpPolicy(
  orgId: string,
  signal?: AbortSignal
): Promise<ApiResponse<IpPolicy, PolicyMeta>> {
  const res = await request<IpPolicy, PolicyMeta>(
    `/v1/orgs/${orgId}/access-policy/ip`,
    signal ? { signal } : {}
  );
  rememberEtag(etagKey.ipPolicy(orgId), res.etag);
  return res;
}

export async function updateIpPolicy(
  orgId: string,
  body: IpPolicyUpdateRequest,
  ifMatch: string,
  idempotencyKey: string
): Promise<ApiResponse<IpPolicy, PolicyMeta>> {
  const res = await request<IpPolicy, PolicyMeta>(`/v1/orgs/${orgId}/access-policy/ip`, {
    method: "PUT",
    body,
    ifMatch,
    idempotencyKey
  });
  rememberEtag(etagKey.ipPolicy(orgId), res.etag);
  return res;
}

/** `data` is null until an owner makes the first claim. */
export async function getProtectedDomain(
  orgId: string,
  signal?: AbortSignal
): Promise<ApiResponse<ProtectedDomain | null, PolicyMeta>> {
  const res = await request<ProtectedDomain | null, PolicyMeta>(
    `/v1/orgs/${orgId}/access-policy/protected-domain`,
    signal ? { signal } : {}
  );
  rememberEtag(etagKey.protectedDomain(orgId), res.etag);
  return res;
}

/**
 * Only `enabled` is sendable — the protected domain itself is derived server-side from
 * the verified owner email, so the browser can never propose a domain to claim.
 */
export async function updateProtectedDomain(
  orgId: string,
  enabled: boolean,
  ifMatch: string,
  idempotencyKey: string
): Promise<ApiResponse<ProtectedDomain | null, PolicyMeta>> {
  const res = await request<ProtectedDomain | null, PolicyMeta>(
    `/v1/orgs/${orgId}/access-policy/protected-domain`,
    { method: "PUT", body: { enabled }, ifMatch, idempotencyKey }
  );
  rememberEtag(etagKey.protectedDomain(orgId), res.etag);
  return res;
}

// ---------------------------------------------------- organization extension policy

export async function getExtensionPolicy(
  orgId: string,
  signal?: AbortSignal
): Promise<ApiResponse<ExtensionPolicy, ExtensionPolicyMeta>> {
  const res = await request<ExtensionPolicy, ExtensionPolicyMeta>(
    `/v1/orgs/${orgId}/extension-policy`,
    signal ? { signal } : {}
  );
  rememberEtag(etagKey.extensionPolicy(orgId), res.etag);
  return res;
}

export async function updateExtensionPolicy(
  orgId: string,
  body: ExtensionPolicyUpdateRequest,
  ifMatch: string,
  idempotencyKey: string
): Promise<ApiResponse<ExtensionPolicy, ExtensionPolicyMeta>> {
  const res = await request<ExtensionPolicy, ExtensionPolicyMeta>(
    `/v1/orgs/${orgId}/extension-policy`,
    {
      method: "PUT",
      body,
      ifMatch,
      idempotencyKey
    }
  );
  rememberEtag(etagKey.extensionPolicy(orgId), res.etag);
  return res;
}

export async function getExtensionPolicyCatalog(
  orgId: string,
  signal?: AbortSignal
): Promise<ApiResponse<ExtensionPolicyCatalog>> {
  return request<ExtensionPolicyCatalog>(
    `/v1/orgs/${orgId}/extension-policy/catalog`,
    signal ? { signal } : {}
  );
}

// ------------------------------------------- support organization recovery (admin)

export type SupportOrganizationSort = "name" | "created" | "plan";
export type SupportOrganizationStatus = "all" | "active" | "suspended";
export type SupportOrganizationProvisioningSource = "all" | "customer" | "epidev_admin";
export type SupportOrganizationPolicyFilter = "all" | "ip" | "extension" | "domain";

export interface SupportOrganizationQuery {
  query?: string;
  limit?: number;
  cursor?: string;
  status?: SupportOrganizationStatus;
  provisioningSource?: SupportOrganizationProvisioningSource;
  policy?: SupportOrganizationPolicyFilter;
  sort?: SupportOrganizationSort;
  direction?: SortDirection;
}

export async function listSupportOrganizations(
  query: SupportOrganizationQuery = {},
  signal?: AbortSignal
): Promise<CursorPage<SupportOrganization>> {
  const res = await request<SupportOrganization[], PageMeta>("/v1/admin/organizations", {
    query: { ...query },
    ...(signal ? { signal } : {})
  });
  return { items: res.data, nextCursor: res.meta.nextCursor, totalCount: res.meta.totalCount };
}

/**
 * Support may only DISABLE enforcement — there is no support write path for the ranges
 * themselves. The route answers with the resulting organization + policy, which is
 * intentionally broader than the OpenAPI `Mutation` envelope (see CONTRACT_NOTES N-17).
 */
export interface SupportIpDisableResult {
  disabled: boolean;
  eventId: string;
  organization: { id: string; name: string; slug: string };
  policy: IpPolicy;
}

export async function supportDisableIpPolicy(
  orgId: string,
  body: { confirmation: string; reasonReference: string },
  idempotencyKey: string
): Promise<SupportIpDisableResult> {
  const res = await request<SupportIpDisableResult>(
    `/v1/admin/organizations/${encodeURIComponent(orgId)}/ip-policy/disable`,
    { method: "POST", body, idempotencyKey }
  );
  return res.data;
}

export interface SupportExtensionPolicyDisableResult {
  disabled: boolean;
  eventId: string;
  organization: { id: string; name: string; slug: string };
  policy: ExtensionPolicy;
}

export async function supportDisableExtensionPolicy(
  orgId: string,
  body: { confirmation: string; reasonReference: string },
  idempotencyKey: string
): Promise<SupportExtensionPolicyDisableResult> {
  const res = await request<SupportExtensionPolicyDisableResult>(
    `/v1/admin/organizations/${encodeURIComponent(orgId)}/extension-policy/disable`,
    { method: "POST", body, idempotencyKey }
  );
  return res.data;
}

export interface SupportProtectedDomainOverrideResult {
  policy: ProtectedDomain;
  previousOrganizationId: string | null;
  releasedDomain: string | null;
}

export async function supportOverrideProtectedDomain(
  orgId: string,
  body: { domain: string; confirmation: string; reasonReference: string },
  idempotencyKey: string
): Promise<SupportProtectedDomainOverrideResult> {
  const res = await request<SupportProtectedDomainOverrideResult>(
    `/v1/admin/organizations/${encodeURIComponent(orgId)}/protected-domain/override`,
    { method: "POST", body, idempotencyKey }
  );
  return res.data;
}

export interface SupportOrganizationCreationResult {
  organization: { id: string; name: string; slug: string; version: number };
  owner: { id: string; email: string; displayName: string; emailVerified: boolean; provisioned: boolean };
  entitlementCustomerId: string | null;
  entitlement: ManagedEntitlement | null;
  onboarding: {
    required: boolean;
    delivery: "not_required" | "queued" | "deduplicated" | "rate_limited";
  };
  supportEventId: string;
  replayed: boolean;
}

/** Creates a Portal organization, optionally provisioning and binding its initial entitlement customer. */
export async function supportCreateOrganization(
  body: SupportOrganizationCreationRequest,
  idempotencyKey: string
): Promise<SupportOrganizationCreationResult> {
  const res = await request<SupportOrganizationCreationResult>("/v1/admin/organizations", {
    method: "POST",
    body,
    idempotencyKey
  });
  return res.data;
}

/** Provisions the first entitlement customer on an existing organization. */
export async function supportCreateOrganizationCustomer(
  orgId: string,
  body: SupportOrganizationCustomerRequest,
  idempotencyKey: string
): Promise<SupportOrganizationCreationResult> {
  const res = await request<SupportOrganizationCreationResult>(
    `/v1/admin/organizations/${encodeURIComponent(orgId)}/entitlement-customer`,
    { method: "POST", body, idempotencyKey }
  );
  return res.data;
}

export interface SupportConversionResult {
  organization: { id: string; name: string; slug: string };
  owner: { id: string; email: string };
  license: { accessLevel: string; seatLimit: number | null };
  supportEventId: string;
  replayed: boolean;
}

/**
 * Idempotent: the organization id is derived from the customer id server-side, so a
 * replayed conversion returns the same organization instead of creating a second one.
 */
export async function supportConvertCustomerToOrganization(
  customerId: string,
  body: { ownerEmail: string; organizationName: string; confirmation: string; reasonReference: string },
  idempotencyKey: string
): Promise<SupportConversionResult> {
  const res = await request<SupportConversionResult>(
    `/v1/admin/entitlement-customers/${encodeURIComponent(customerId)}/convert-to-organization`,
    { method: "POST", body, idempotencyKey }
  );
  return res.data;
}

// ---------------------------------------------------------- domains / saml / etc.

export async function listDomains(orgId: string, signal?: AbortSignal): Promise<Domain[]> {
  const res = await request<Domain[]>(`/v1/orgs/${orgId}/domains`, signal ? { signal } : {});
  return res.data;
}

export async function createDomain(
  orgId: string,
  domain: string,
  idempotencyKey: string
): Promise<DomainChallenge> {
  const res = await request<DomainChallenge>(`/v1/orgs/${orgId}/domains`, {
    method: "POST",
    body: { domain },
    idempotencyKey
  });
  return res.data;
}

export async function verifyDomain(
  orgId: string,
  domainId: string,
  idempotencyKey: string
): Promise<Domain> {
  const res = await request<Domain>(`/v1/orgs/${orgId}/domains/${domainId}/verify`, {
    method: "POST",
    idempotencyKey
  });
  return res.data;
}

export async function removeDomain(
  orgId: string,
  domainId: string,
  idempotencyKey: string
): Promise<{ removed: boolean }> {
  const res = await request<{ removed: boolean }>(`/v1/orgs/${orgId}/domains/${domainId}`, {
    method: "DELETE",
    idempotencyKey
  });
  return res.data;
}

export async function getSamlConnection(
  orgId: string,
  signal?: AbortSignal
): Promise<ApiResponse<SamlConnection>> {
  const res = await request<SamlConnection>(`/v1/orgs/${orgId}/sso/saml`, signal ? { signal } : {});
  rememberEtag(etagKey.saml(orgId), res.etag);
  return res;
}

export async function updateSamlConnection(
  orgId: string,
  body: {
    idpEntityId: string;
    ssoUrl: string;
    idpSigningCertificates?: string[];
    attributeMapping?: { email?: string; displayName?: string };
    jitEnabled?: boolean;
    jitDefaultRole?: "admin" | "member";
    jitAutoAssignSeat?: boolean;
    enforceSso?: boolean;
  },
  ifMatch: string,
  idempotencyKey: string
): Promise<SamlConnection> {
  const res = await request<SamlConnection>(`/v1/orgs/${orgId}/sso/saml`, {
    method: "PUT",
    body,
    ifMatch,
    idempotencyKey
  });
  rememberEtag(etagKey.saml(orgId), res.etag);
  return res.data;
}

export async function importSamlMetadata(
  orgId: string,
  body: { url: string } | { xml: string },
  ifMatch: string,
  idempotencyKey: string
): Promise<SamlConnection> {
  const res = await request<SamlConnection>(`/v1/orgs/${orgId}/sso/saml/metadata-import`, {
    method: "POST",
    body,
    ifMatch,
    idempotencyKey
  });
  rememberEtag(etagKey.saml(orgId), res.etag);
  return res.data;
}

export async function testSaml(
  orgId: string,
  idempotencyKey: string
): Promise<{ redirectUrl: string; expiresInSeconds: number }> {
  const res = await request<{ redirectUrl: string; expiresInSeconds: number }>(
    `/v1/orgs/${orgId}/sso/saml/test`,
    { method: "POST", idempotencyKey }
  );
  return res.data;
}

export async function activateSaml(
  orgId: string,
  enforceSso: boolean,
  ifMatch: string,
  idempotencyKey: string
): Promise<SamlConnection> {
  const res = await request<SamlConnection>(`/v1/orgs/${orgId}/sso/saml/activate`, {
    method: "POST",
    body: { enforceSso },
    ifMatch,
    idempotencyKey
  });
  rememberEtag(etagKey.saml(orgId), res.etag);
  return res.data;
}

export async function disableSaml(
  orgId: string,
  ifMatch: string,
  idempotencyKey: string
): Promise<SamlConnection> {
  const res = await request<SamlConnection>(`/v1/orgs/${orgId}/sso/saml/disable`, {
    method: "POST",
    ifMatch,
    idempotencyKey
  });
  rememberEtag(etagKey.saml(orgId), res.etag);
  return res.data;
}

export async function getSettings(orgId: string, signal?: AbortSignal): Promise<ApiResponse<Settings>> {
  const res = await request<Settings>(`/v1/orgs/${orgId}/settings`, signal ? { signal } : {});
  rememberEtag(etagKey.settings(orgId), res.etag);
  return res;
}

export async function updateSettings(
  orgId: string,
  body: { name?: string; securityPolicy?: Record<string, unknown> },
  ifMatch: string,
  idempotencyKey: string
): Promise<Settings> {
  const res = await request<Settings>(`/v1/orgs/${orgId}/settings`, {
    method: "PATCH",
    body,
    ifMatch,
    idempotencyKey
  });
  rememberEtag(etagKey.settings(orgId), res.etag);
  return res.data;
}

export interface AuditQuery {
  cursor?: string;
  limit?: number;
  action?: string;
  result?: "success" | "failure";
}

export async function listAudit(
  orgId: string,
  query: AuditQuery,
  signal?: AbortSignal
): Promise<ApiResponse<AuditEvent[], PageMeta>> {
  return request<AuditEvent[], PageMeta>(`/v1/orgs/${orgId}/audit`, {
    query: { ...query },
    ...(signal ? { signal } : {})
  });
}

export type { ActivationDelivery };
